import '../lib/apiConfig'; // Initialize API config
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { supabase } from '../lib/supabase';
import WaitlistModal from '../components/WaitlistModal';

function Signup() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [email, setEmail] = useState('');
    const [isWaitlistModalOpen, setIsWaitlistModalOpen] = useState(false);

    // Handle email verification callback
    useEffect(() => {
        const verified = searchParams.get('verified');
        const errorParam = searchParams.get('error');

        if (errorParam === 'verification_failed') {
            const messageParam = searchParams.get('message');
            const displayMessage = messageParam
                ? messageParam
                : 'Email verification failed. Please try again or request a new verification email.';
            setError(displayMessage);
            setSearchParams({}); // Clear the error parameter
            return;
        }

        if (verified === 'true') {
            console.log('Email verification confirmed - checking auth state...');
                setSuccess('Email verified successfully!');
                setSearchParams({}); // Clear the verified parameter

            // Check if user has session and redirect to onboarding
            supabase.auth.getSession().then(({ data: { session } }) => {
                if (session?.user) {
                    // User is authenticated, redirect to onboarding
                    navigate('/onboarding');
                }
            });
        }
    }, [searchParams, navigate, setSearchParams]);

    // Check for existing session
    useEffect(() => {
        supabase.auth.getSession().then(({ data: { session } }) => {
            if (session?.user) {
                const redirectParam = searchParams.get('redirect');
                const target = redirectParam ? decodeURIComponent(redirectParam) : '/onboarding';
                navigate(target.startsWith('/') ? target : `/${target}`);
            }
        });
    }, [navigate, searchParams]);

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleGoogleSignup = async () => {
        setLoading(true);
        setError('');
        setSuccess('');

        try {
            const siteUrl = window.location.origin;
            const { error: signInError } = await supabase.auth.signInWithOAuth({
                provider: 'google',
                options: {
                    redirectTo: `${siteUrl}/onboarding`,
                },
            });

            if (signInError) {
                setError(signInError.message || 'Failed to sign in with Google');
                setLoading(false);
            }
            // Note: User will be redirected to Google, so we don't set loading to false here
        } catch (error) {
            console.error('Error signing in with Google:', error);
            setError('An error occurred. Please try again.');
            setLoading(false);
        }
    };

    const handleEmailSignup = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!email) {
            setError('Please enter your email address');
            return;
        }

        if (!validateEmail(email)) {
            setError('Please enter a valid email address');
            return;
        }

        setLoading(true);

        try {
            // Sign up with email (Supabase will send magic link). Always redirect to /onboarding so the URL is allowed in Supabase; claim flow uses localStorage (pending_claim) to send user back to claim page.
            const siteUrl = window.location.origin;
            const { error: signUpError } = await supabase.auth.signInWithOtp({
                email,
                options: {
                    emailRedirectTo: `${siteUrl}/onboarding`,
                },
            });

            if (signUpError) {
                setError(signUpError.message || 'Failed to send verification email');
                setLoading(false);
                return;
            }

            setSuccess('Check your email for a sign-in link! Click the link to complete your account setup.');
            setLoading(false);
        } catch (error) {
            console.error('Error signing up:', error);
            setError('An error occurred. Please try again.');
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-background flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8 bg-card p-8 rounded-lg shadow-md">
                <div>
                    <h2 className="text-3xl font-bold text-center text-foreground">
                        Create Your Account
                    </h2>
                    <p className="mt-2 text-center text-sm text-muted-foreground">
                        Sign up quickly with Google or use your email
                    </p>
                </div>

                {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                        {error}
                    </div>
                )}

                {success && (
                    <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
                        {success}
                    </div>
                )}

                        <div className="space-y-4">
                    {/* Google OAuth Button */}
                    <Button
                        type="button"
                        onClick={handleGoogleSignup}
                        disabled={loading}
                        className="w-full"
                        variant="outline"
                    >
                        <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                            <path
                                fill="currentColor"
                                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                            />
                            <path
                                fill="currentColor"
                                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                            />
                            <path
                                fill="currentColor"
                                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                            />
                            <path
                                fill="currentColor"
                                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                            />
                        </svg>
                        {loading ? 'Signing in...' : 'Continue with Google'}
                    </Button>

                    <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-gray-300" />
                        </div>
                        <div className="relative flex justify-center text-sm">
                            <span className="px-2 bg-card text-muted-foreground">Or</span>
                        </div>
                    </div>

                    {/* Email Signup Form */}
                    <form onSubmit={handleEmailSignup} className="space-y-4">
                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-foreground">
                                    Email Address
                                </label>
                                <Input
                                    id="email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@example.com"
                                    className="mt-1"
                                    required
                                disabled={loading}
                                />
                            </div>
                            <Button
                                type="submit"
                                disabled={loading}
                                className="w-full"
                            >
                            {loading ? 'Sending...' : 'Continue with Email'}
                            </Button>
                    </form>
                        </div>

                <div className="mt-4 text-center">
                    <p className="text-sm text-muted-foreground">
                        Already have an account?{' '}
                        <a href="/login" className="text-brand-orange hover:text-brand-orange-hover font-medium font-ui">
                            Log in
                        </a>
                                </p>
                            </div>

                <div className="mt-4 text-center">
                    <p className="text-sm text-muted-foreground mb-2">
                        Don't have access?
                    </p>
                                    <button
                                        type="button"
                        onClick={() => setIsWaitlistModalOpen(true)}
                        className="text-sm text-brand-orange hover:text-brand-orange-hover font-medium font-ui underline"
                    >
                        Join the Waitlist
                                    </button>
                                </div>

                <WaitlistModal isOpen={isWaitlistModalOpen} onClose={() => setIsWaitlistModalOpen(false)} />
            </div>
        </div>
    );
}

export default Signup;
